import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, Search, User, Calendar, RefreshCw, ChevronDown, ChevronRight, ExternalLink } from "lucide-react";

/**
 * Panel admin de errores JS reportados por el frontend
 * (`window.error`, `unhandledrejection` y ErrorBoundary de React).
 *
 * Permite filtrar por email de usuario, rango de fechas y texto libre,
 * ver el stack completo, y detectar rápido usuarios con crashes en bucle.
 */
export default function AdminErrorsPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [topUsers, setTopUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // id del row expandido

  const [email, setEmail] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  const [query, setQuery] = useState("");
  const [limit, setLimit] = useState(100);

  const load = () => {
    setLoading(true);
    const params = { limit };
    if (email) params.email = email;
    if (since) params.since = since;
    if (until) params.until = until;
    if (query) params.query = query;
    api
      .get("/admin/error-logs", { params })
      .then((r) => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setTopUsers(r.data.top_users || []);
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "No se pudo cargar el listado"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clearFilters = () => {
    setEmail(""); setSince(""); setUntil(""); setQuery(""); setLimit(100);
    setTimeout(load, 0);
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
  };

  return (
    <div className="max-w-7xl mx-auto p-6 sm:p-8" data-testid="admin-errors-page">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-3">Admin · Diagnóstico</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter text-slate-950 flex items-center gap-3">
          <AlertTriangle className="h-8 w-8 text-rose-500" strokeWidth={1.8} />
          Errores JS del frontend
        </h1>
        <p className="text-slate-500 text-sm mt-2 max-w-2xl">
          Crashes JS reportados por los clientes reales — window.error, unhandledrejection y ErrorBoundary de React. Usa los filtros para investigar incidencias concretas.
        </p>
      </div>

      {/* Top usuarios con más crashes */}
      {topUsers.length > 0 && (
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
            <div className="text-[10px] uppercase font-bold text-rose-700 mb-2">Top usuarios con más crashes</div>
            <div className="space-y-1.5">
              {topUsers.map((u, i) => (
                <button
                  key={u.email + i}
                  onClick={() => { setEmail(u.email === "anónimo" ? "" : u.email); setTimeout(load, 0); }}
                  className="w-full text-left flex items-center justify-between text-xs hover:bg-white/60 px-2 py-1 rounded"
                  data-testid={`top-user-${i}`}
                >
                  <span className="text-slate-800 font-medium truncate">{u.email}</span>
                  <span className="font-mono-num font-bold text-rose-900 ml-2">{u.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="bg-slate-100 border border-slate-200 rounded-lg p-4">
            <div className="text-[10px] uppercase font-bold text-slate-600 mb-2">Resumen</div>
            <div className="text-slate-900 text-sm">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-3xl font-bold font-mono-num" data-testid="errors-total">{total}</span>
                <span className="text-xs uppercase tracking-wider text-slate-500">errores</span>
              </div>
              <div className="text-xs text-slate-500">
                {items.length} mostrados en este listado · {topUsers.length} usuarios únicos entre los más afectados
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-slate-200 rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="lg:col-span-2">
            <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Email del usuario</label>
            <div className="relative">
              <User className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="parcial, ej: fmx"
                className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                data-testid="filter-email"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Desde</label>
            <input
              type="datetime-local"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="filter-since"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Hasta</label>
            <input
              type="datetime-local"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="w-full px-2 py-2 border border-slate-300 rounded-md text-sm bg-white"
              data-testid="filter-until"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase font-semibold text-slate-500 mb-1 block">Texto (mensaje/URL/stack)</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="TypeError…"
                className="w-full pl-8 pr-3 py-2 border border-slate-300 rounded-md text-sm bg-white"
                data-testid="filter-query"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 mt-3 flex-wrap">
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 rounded-md text-xs font-bold disabled:opacity-50"
            data-testid="apply-filters"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            {loading ? "Cargando…" : "Aplicar filtros"}
          </button>
          <button
            onClick={clearFilters}
            className="text-xs text-slate-500 hover:text-slate-800 px-2"
            data-testid="clear-filters"
          >
            Limpiar
          </button>
          <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
            <span>Máx.</span>
            <select
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              className="px-2 py-1 border border-slate-300 rounded bg-white text-slate-800"
            >
              {[50, 100, 200, 500].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla */}
      {items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-200 rounded-lg p-12 text-center text-slate-400 text-sm" data-testid="errors-empty">
          {loading ? "Cargando…" : "No hay errores para los filtros aplicados."}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="errors-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left p-3 font-semibold w-40">Fecha</th>
                <th className="text-left p-3 font-semibold w-48">Usuario</th>
                <th className="text-left p-3 font-semibold">Mensaje</th>
                <th className="text-left p-3 font-semibold w-40">URL</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const isOpen = expanded === it.id;
                return (
                  <>
                    <tr
                      key={it.id}
                      className={`border-t border-slate-200 hover:bg-slate-50 cursor-pointer ${isOpen ? "bg-rose-50/40" : ""}`}
                      onClick={() => setExpanded(isOpen ? null : it.id)}
                      data-testid={`error-row-${it.id}`}
                    >
                      <td className="p-3 text-xs text-slate-600 font-mono-num">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3 w-3 text-slate-400" />
                          {fmtDate(it.created_at)}
                        </div>
                      </td>
                      <td className="p-3 text-xs text-slate-700 truncate max-w-[192px]" title={it.user_email || "anónimo"}>
                        {it.user_email || <span className="italic text-slate-400">anónimo</span>}
                      </td>
                      <td className="p-3 text-xs text-slate-900 truncate max-w-md" title={it.message}>{it.message}</td>
                      <td className="p-3 text-xs text-slate-500 truncate max-w-[160px]" title={it.url}>
                        {it.url ? (
                          <a href={it.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:underline" onClick={(e) => e.stopPropagation()}>
                            <ExternalLink className="h-3 w-3" />
                            {it.url.replace(/^https?:\/\//, "").slice(0, 40)}
                          </a>
                        ) : "—"}
                      </td>
                      <td className="p-3 text-slate-400">
                        {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr key={it.id + "-detail"} className="bg-slate-50 border-t border-slate-200">
                        <td colSpan={5} className="p-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
                            <div>
                              <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Stack trace</div>
                              <pre className="bg-white border border-slate-200 rounded-md p-3 text-[11px] text-slate-700 overflow-auto max-h-64 whitespace-pre-wrap">
                                {it.stack || "(sin stack)"}
                              </pre>
                            </div>
                            <div>
                              <div className="text-[10px] uppercase font-semibold text-slate-500 mb-1">Component stack</div>
                              <pre className="bg-white border border-slate-200 rounded-md p-3 text-[11px] text-slate-700 overflow-auto max-h-64 whitespace-pre-wrap">
                                {it.component_stack || "(sin component stack)"}
                              </pre>
                              <div className="text-[10px] uppercase font-semibold text-slate-500 mt-3 mb-1">User agent</div>
                              <div className="bg-white border border-slate-200 rounded-md p-2 text-[11px] text-slate-600">
                                {it.user_agent || "—"}
                              </div>
                              {it.ip && (
                                <div className="mt-2 text-[10px] text-slate-500">
                                  IP: <span className="font-mono-num">{it.ip}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
